import { Directive, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IAudioService, PlaybackState } from '@nimbus-library/features/shared/services/media/i-audio.service';

@Directive()
export class AudioService implements IAudioService, OnDestroy {
  public static activeAudioService: AudioService;
  public audio: HTMLAudioElement;
  public loaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public duration$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public elapsedSeconds$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public state$: BehaviorSubject<PlaybackState> = new BehaviorSubject<PlaybackState>(PlaybackState.Pause);

  public constructor(data: string, createAudio: () => HTMLAudioElement) {
    this.audio = createAudio();
    this.load(data);

    this.audio.onloadeddata = (): void => {
      this.loaded$.next(true);
      this.duration$.next(this.audio.duration);
    };
    this.audio.onplay = (): void => {
      this.state$.next(PlaybackState.Play);
    };
    this.audio.onpause = (): void => {
      if (this.elapsedSeconds$.value !== this.duration$.value) {
        this.state$.next(PlaybackState.Pause);
      }
    };
    this.audio.ontimeupdate = (): void => {
      this.elapsedSeconds$.next(this.audio.currentTime);
    };
    this.audio.onended = (): void => {
      this.state$.next(PlaybackState.Finish);
    };
    this.audio.onerror = this.handleError.bind(this);
  }

  public ngOnDestroy(): void {
    this.audio.remove();

    this.elapsedSeconds$.complete();
    this.duration$.complete();
    this.loaded$.complete();
    this.state$.complete();

    if (AudioService.activeAudioService === this) {
      AudioService.activeAudioService = null;
    }
  }

  public play(): void {
    this.state$.next(PlaybackState.Play);
    this.audio.play();
    if (
      AudioService.activeAudioService &&
      AudioService.activeAudioService !== this &&
      AudioService.activeAudioService.state$.value === PlaybackState.Play
    ) {
      AudioService.activeAudioService.pause();
    }
    AudioService.activeAudioService = this;
  }

  public pause(): void {
    if (this.state$.value === PlaybackState.Pause) {
      return;
    }
    this.audio.pause();
  }

  public reset(): void {
    if (this.state$.value === PlaybackState.Reset) {
      return;
    }
    this.audio.pause();
    this.audio.currentTime = 0;
    this.elapsedSeconds$.next(0);
    this.state$.next(PlaybackState.Reset);
  }

  public finish(): void {
    if (this.state$.value === PlaybackState.Finish) {
      return;
    }
    if (this.state$.value === PlaybackState.Play) {
      this.audio.pause();
    }
    this.audio.currentTime = this.duration$.value;
    this.elapsedSeconds$.next(this.audio.currentTime);
    this.state$.next(PlaybackState.Finish);
  }

  public setCurrentTime(seconds: number): void {
    this.audio.currentTime = seconds;
    this.elapsedSeconds$.next(seconds);
  }

  private async load(data: string): Promise<void> {
    const response = await fetch(data);
    const blob = await response.blob();
    const audioUrl = URL.createObjectURL(blob);
    this.audio.src = audioUrl;
    this.audio.load();
  }

  private handleError(event: ErrorEvent): void {
    const audio = event.target as HTMLAudioElement;
    const error = audio.error;

    let message = 'Unknown error';

    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          message = 'Loading aborted';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          message = 'Network error';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          message = 'Audio decode failed';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message = `Format not supported or source invalid. URL: ${audio.src}`;
          break;
      }
    }

    // eslint-disable-next-line no-console
    console.error('Audio error:', {
      code: error?.code,
      message,
      url: audio.src,
      state: audio.readyState,
    });

    if (!this.loaded$.value) {
      this.loaded$.error(new Error(message));
    }
  }
}
